// deps
import React, { memo, ReactElement, useEffect, useRef, useState, ReactNode, useCallback, useMemo } from "react";
import { isValidElement } from "react";

// internal deps
import { isEmpty, isNil } from "@swiggy-private/js-utils";
import { whichTransitionEndEvent } from "@swiggy-private/payment-core";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

//compnents
import { AriaHidden, getCloudinaryUrl } from "@swiggy-private/core-ui";

// test-ids
import { TICK_SCREEN_TEST_IDS } from "@swiggy-private/payment-test-ids";

// constants
import { ACCESSIBILITY_ROLES } from "@payment-ui/constants/index";
import { TICK_ACCENT, TICK_STATES, SCOOTER_ICON } from "../constants";

// styles
import {
    Container,
    Wrapper,
    TickSvg,
    CircleWrapper,
    Circle,
    TickPath,
    TickShadowPath,
    InfoContainer,
    MainTextContainer,
    SubTextContainer,
    SubTextChange,
    IntermediateTextContainer,
    ScooterContainer,
} from "./styles/TickScreen.styled";

const RADIUS = 48;

export interface TickScreenUiProps {
    tickState: TICK_STATES;
    postSuccessCb?: VoidFunction;
    mainText?: string;
    subText?: string;
    subTextNode?: ReactNode;
    intermediateText?: string;
    customSuccessScreen?: boolean;
    a11yStatus?: string;
    tickAccent?: TICK_ACCENT;
    showBottomAnimation?: boolean;
    cardBrand?: string;
    successTimeout: number;
    tagElement?: ReactNode;
    isSuper?: boolean;
    isSwiggyMoney?: boolean;
    isJioHotstarFlow?: boolean;
}

function TickScreenUi(props: TickScreenUiProps): ReactElement {
    const {
        postSuccessCb,
        customSuccessScreen = false,
        a11yStatus,
        tickState,
        mainText,
        intermediateText,
        tickAccent,
        showBottomAnimation,
        tagElement,
        isSuper = false,
        isSwiggyMoney = false,
        isJioHotstarFlow = false,
    } = props;
    const [isPostEventFired, setPostEventFired] = useState(false);
    const infoRef = useRef<HTMLDivElement | null>(null);
    const tickRef = useRef<SVGPathElement | null>(null);

    const onPostSuccess = useCallback((): void => {
        setPostEventFired(true);
        postSuccessCb && postSuccessCb();
    }, [postSuccessCb]);

    const callbackfn = useCallback(() => {
        if (isPostEventFired) {
            return;
        }
        onPostSuccess();
    }, [isPostEventFired, onPostSuccess]);

    // post success
    useEffect(() => {
        if (tickState !== TICK_STATES.SUCCESS || typeof postSuccessCb !== "function") {
            return;
        }

        if (customSuccessScreen) {
            onPostSuccess();
        }

        const tickElement = tickRef.current;
        const transitionEndEvent = whichTransitionEndEvent();

        if (tickElement && transitionEndEvent) {
            tickElement.addEventListener(transitionEndEvent, onPostSuccess, false);

            // manually firing post success in case transitionevent is not fired
            const timeout = setTimeout(callbackfn, props.successTimeout);
            return (): void => {
                tickElement.removeEventListener(transitionEndEvent, onPostSuccess, false);
                clearTimeout(timeout);
            };
        }
    }, [tickState, postSuccessCb, onPostSuccess, callbackfn, customSuccessScreen, props.successTimeout]);

    const showTickScreen = useMemo(
        () => !customSuccessScreen && tickState === TICK_STATES.SUCCESS,
        [customSuccessScreen, tickState],
    );
    const showLoadingScreen = useMemo(
        () => customSuccessScreen || tickState === TICK_STATES.LOADING,
        [customSuccessScreen, tickState],
    );

    const subTextEl = useMemo((): ReactNode | null => {
        if (!isEmpty(props.subTextNode) && isValidElement(props.subTextNode)) {
            return (
                <SubTextContainer
                    $showLoadingScreen={showLoadingScreen}
                    $showTickScreen={showTickScreen}
                    $isSwiggyMoney={isSwiggyMoney}
                >
                    <Text
                        variant={isJioHotstarFlow ? "V2.body1.neutral.medium" : "V2.body2.neutral.medium"}
                        color={isJioHotstarFlow ? "text_Med_Emphasis" : "text_Low_Emphasis"}
                        data-testid={TICK_SCREEN_TEST_IDS.SUB_TEXT}
                    >
                        <SubTextChange>{props.subTextNode}</SubTextChange>
                    </Text>
                </SubTextContainer>
            );
        }

        if (props.subText == null) {
            return null;
        }

        return (
            <SubTextContainer
                $showLoadingScreen={showLoadingScreen}
                $showTickScreen={showTickScreen}
                $isSwiggyMoney={isSwiggyMoney}
            >
                {!isNil(props.subText) && (
                    <Text
                        variant="V2.body2.neutral.medium"
                        color="text_Low_Emphasis"
                        data-testid={TICK_SCREEN_TEST_IDS.SUB_TEXT}
                    >
                        {props.subText}
                    </Text>
                )}
            </SubTextContainer>
        );
    }, [props.subTextNode, props.subText, showLoadingScreen, showTickScreen, isSwiggyMoney]);

    return (
        <Container $isJioHotstarFlow={isJioHotstarFlow} data-testid={TICK_SCREEN_TEST_IDS.CONTAINER}>
            <Wrapper>
                <TickSvg
                    $showLoadingScreen={showLoadingScreen}
                    viewBox="0 0 100 100"
                    data-testid={TICK_SCREEN_TEST_IDS.SVG}
                >
                    <CircleWrapper
                        $tickAccent={tickAccent}
                        $showTickScreen={showTickScreen}
                        cx="50"
                        cy="50"
                        r={RADIUS}
                    />
                    <Circle
                        $tickAccent={tickAccent}
                        $showTickScreen={showTickScreen}
                        $showLoadingScreen={showLoadingScreen}
                        $isSuper={isSuper}
                        $isSwiggyMoney={isSwiggyMoney}
                        cx="50"
                        cy="50"
                        r={RADIUS}
                    />
                    {showTickScreen && (
                        <>
                            <TickShadowPath d="M25,50 L40,65 L75,35" />
                            <TickPath
                                ref={tickRef}
                                $showTickScreen={showTickScreen}
                                $isSuper={isSuper}
                                $isSwiggyMoney={isSwiggyMoney}
                                d="M25,50 L40,65 L75,35"
                            />
                        </>
                    )}
                </TickSvg>
                <InfoContainer
                    ref={infoRef}
                    $showLoadingScreen={showLoadingScreen}
                    $showTickScreen={showTickScreen}
                    $isSuper={isSuper}
                    $isSwiggyMoney={isSwiggyMoney}
                    $isJioHotstarFlow={isJioHotstarFlow}
                    tabIndex={0}
                    role={ACCESSIBILITY_ROLES.ALERT}
                    aria-live="assertive"
                    aria-label={a11yStatus}
                >
                    {!isNil(mainText) && (
                        <MainTextContainer
                            $showLoadingScreen={showLoadingScreen}
                            $showTickScreen={showTickScreen}
                            $isSwiggyMoney={isSwiggyMoney}
                            $isJioHotstarFlow={isJioHotstarFlow}
                        >
                            <Text
                                variant="V2.heading1.neutral.bold"
                                color="text_Highest_Emphasis"
                                data-testid={TICK_SCREEN_TEST_IDS.MAIN_TEXT}
                            >
                                {mainText}
                            </Text>
                        </MainTextContainer>
                    )}

                    {!isNil(intermediateText) && (
                        <IntermediateTextContainer $showLoadingScreen={showLoadingScreen}>
                            <Text
                                variant="V2.body2.neutral.medium"
                                color="text_Highest_Emphasis"
                                data-testid={TICK_SCREEN_TEST_IDS.INTERMEDIATE_TEXT}
                            >
                                {intermediateText}
                            </Text>
                        </IntermediateTextContainer>
                    )}
                    {subTextEl}
                    {tagElement ? tagElement : null}
                </InfoContainer>
            </Wrapper>

            {showTickScreen && showBottomAnimation && (
                <ScooterContainer>
                    <AriaHidden as="div">
                        <img
                            crossOrigin="anonymous"
                            src={getCloudinaryUrl(SCOOTER_ICON.imageId, {
                                width: SCOOTER_ICON.width,
                                height: SCOOTER_ICON.height,
                            })}
                        />
                    </AriaHidden>
                </ScooterContainer>
            )}
        </Container>
    );
}

export default memo(TickScreenUi);
